import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';

import { AuthAppService } from './auth-app/auth-app.service';
import { BusinessMainService } from './business-main.service';
import { Constants } from './constants';
import { UserService } from './user.service';

/** Who the workforce token is minted for: the business itself (owner) or one practitioner in it. */
export type WorkforceScopeType = 'business' | 'practitioner';

/**
 * One entry of `businesses` in the get-dashboard-business-detail response.
 * Only the fields this service reads are typed; the API returns more (name, address, workcover flags…).
 */
interface DashboardBusiness {
  uuid: string;
  slug?: string;
  // The business is on workforce (Corporate Care); without it no workforce token is minted at all
  workforce_enabled?: boolean;
  // The business also runs workforce assessments; widens the scopes requested for the token
  workforce_assessment_enabled?: boolean;
}

/**
 * The parts of the get-dashboard-business-detail response this service relies on.
 *
 * Note: the top-level `workforce` object in that response is intentionally NOT used. The workforce
 * flags are read per business from `businesses`, picking the business currently selected by the
 * user (see resolveBusiness).
 */
interface DashboardWorkforceDetail {
  // Every business available to the user (not just the selected one); the workforce flags live on each entry
  businesses?: DashboardBusiness[];
  // Slug of the business the backend considers active; only a fallback when no business uuid is selected
  active_slug?: string | null;
}

/** Body of POST /token/platform-exchange/. */
interface PlatformExchangeRequest {
  scope_type: WorkforceScopeType;
  // Sent for scope_type 'business'
  business_uuid?: string;
  // Sent for scope_type 'practitioner'
  practitioner_uuid?: string;
  // Space-separated list of scopes; when omitted the backend grants its default (minimal) scope
  scope?: string;
}

/** Response of POST /token/platform-exchange/. */
interface PlatformExchangeResponse {
  access_token: string;
  token_type: string;
  // Lifetime of the token in seconds (5 minutes at the time of writing)
  expires_in: number;
  scope: string;
}

/**
 * Mints and caches the workforce-scoped platform token (POST /api/token/platform-exchange/).
 *
 * Flow:
 *  1. get-dashboard-business-detail is fetched once per selected business (or primed by
 *     AuthCommonService right after login via setDashboardDetail) and cached here.
 *  2. The selected business is looked up in `response.businesses` by the business uuid that is also
 *     sent in the request headers (AuthAppService.getBusinessUUID).
 *  3. If that business has `workforce_enabled`, a token request is built for the owner (business
 *     scope) or the practitioner (practitioner scope) and exchanged for a workforce access token.
 *
 * The token is persisted in localStorage next to the Avaana JWT (AuthAppService) and cleared on
 * logout. It lasts 5 minutes: the service reuses it until shortly before expiry, then mints again
 * on demand.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkforceTokenService {
  // Treat the token as expired 30s early so a request never leaves with a token that dies in flight
  private static readonly EXPIRY_SKEW_MS = 30_000;

  // Cached get-dashboard-business-detail response, so every token mint doesn't refetch it
  private dashboardDetail: DashboardWorkforceDetail | null = null;
  // Business the cached detail was fetched for; a location switch must not reuse another business's flags
  private dashboardDetailBusinessUuid: string | null = null;
  // Practitioner record of the logged-in user, resolved once for practitioner-scoped tokens
  private practitionerUuid: string | null = null;
  // The mint currently in progress; concurrent getToken() callers share it instead of minting twice
  private inFlight$: Observable<string> | null = null;

  constructor(
    private httpClient: HttpClient,
    private authService: AuthAppService,
    private userService: UserService,
    private businessMainService: BusinessMainService
  ) {
    // Drop everything on logout so the next user never sees the previous user's flags or token
    this.authService.isAuthenticated.subscribe((isAuthenticated) => {
      if (!isAuthenticated) {
        this.clear();
      }
    });
  }

  /**
   * Prime the cache from a getDashboardBusinessDetail response already fetched elsewhere
   * (e.g. AuthCommonService after login), saving a second request when the token is minted.
   * The detail is tagged with the business selected right now, so it's refetched after a switch.
   */
  setDashboardDetail(detail: DashboardWorkforceDetail) {
    this.dashboardDetail = detail;
    this.dashboardDetailBusinessUuid = this.authService.getBusinessUUID();
  }

  /**
   * True when the selected business is enabled for workforce, i.e. this user can reach workforce at all.
   * Synchronous: pass a freshly fetched response, or it falls back to the cached one (false if none yet).
   */
  isWorkforceEnabled(detail: DashboardWorkforceDetail | null = this.dashboardDetail): boolean {
    return !!this.resolveBusiness(detail)?.workforce_enabled;
  }

  /** Workforce availability, fetching the dashboard detail when it hasn't been primed yet. */
  checkWorkforceEnabled(): Observable<boolean> {
    return this.resolveDetail().pipe(map((detail) => this.isWorkforceEnabled(detail)));
  }

  /**
   * True when the selected business is enabled for workforce.
   * Same check as checkWorkforceEnabled now that both read the per-business `workforce_enabled` flag.
   */
  checkBusinessWorkforceEnabled(): Observable<boolean> {
    return this.resolveDetail().pipe(map((detail) => !!this.resolveBusiness(detail)?.workforce_enabled));
  }

  /** A valid workforce access token, minting a new one when none is cached or it is near expiry. */
  getToken(): Observable<string> {
    // Reuse the stored token only if it is still comfortably valid AND was minted for the business
    // selected now; the cache key embeds the business uuid as `|<uuid>|` (see mint)
    const cached = this.authService.getWorkforceToken();
    if (cached && cached.expiresAt - WorkforceTokenService.EXPIRY_SKEW_MS > Date.now()) {
      const businessUuid = this.authService.getBusinessUUID();
      if (cached.cacheKey.includes(`|${businessUuid}|`)) {
        return of(cached.token);
      }
    }

    // Start one mint and share it: callers arriving while it runs get the same token, and
    // finalize resets inFlight$ so the next expiry triggers a fresh mint
    if (!this.inFlight$) {
      this.inFlight$ = this.resolveDetail().pipe(
        switchMap((detail) => this.buildRequest(detail)),
        switchMap((request) => this.mint(request)),
        finalize(() => (this.inFlight$ = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.inFlight$;
  }

  /** Forget the cached detail, practitioner and token (called on logout). */
  clear() {
    this.dashboardDetail = null;
    this.dashboardDetailBusinessUuid = null;
    this.practitionerUuid = null;
    this.authService.clearWorkforceToken();
    this.inFlight$ = null;
  }

  /**
   * The dashboard detail for the selected business: the cached copy when it was fetched for this
   * same business, otherwise a fresh getDashboardBusinessDetail call (which also refills the cache).
   */
  private resolveDetail(): Observable<DashboardWorkforceDetail> {
    if (this.dashboardDetail && this.dashboardDetailBusinessUuid === this.authService.getBusinessUUID()) {
      return of(this.dashboardDetail);
    }
    return this.userService.getDashboardBusinessDetail().pipe(tap((detail) => this.setDashboardDetail(detail)));
  }

  /**
   * Builds the platform-exchange request from the selected business's flags and the user's role.
   *
   *  - Owner of a workforce-enabled business       → business-scoped token
   *  - Practitioner of a workforce-enabled business → practitioner-scoped token
   *  - Otherwise                                    → error (no token can be minted)
   *
   * Owner is checked first, so a user who is both owner and practitioner gets the business token.
   */
  private buildRequest(detail: DashboardWorkforceDetail): Observable<PlatformExchangeRequest> {
    // The selected business, picked from all the user's businesses by the uuid in the headers
    const business = this.resolveBusiness(detail);
    const businessUuid = business?.uuid;
    if (!businessUuid) {
      return throwError(() => new Error('Workforce token: no business in context'));
    }

    // Owner: business-scoped token
    if (business.workforce_enabled && this.authService.getloggedUser()?.is_owner) {
      const scopes = [
        'appointments:read',
        'appointments:write',
        'practitioner_assessments:write',
        'practitioner_assessments:read',
        'eip:request_extension',
        'preventive_health:request_extension'
      ];
      // practitioner_assessments:write is practitioner-tokens only — a business token only ever reads.
      if (business.workforce_assessment_enabled) {
        scopes.push('practitioner_assessments:read');
      }
      return of({ scope_type: 'business', business_uuid: businessUuid, scope: scopes.join(' ') });
    }

    // Practitioner: practitioner-scoped token for the logged-in user's practitioner record
    if (business.workforce_enabled && this.authService.getloggedUser()?.is_practitioner) {
      // A practitioner may hold appointments:read only; omitting scope grants exactly that.
      return this.resolvePractitionerUuid(businessUuid).pipe(
        map((practitionerUuid) => {
          // Assessment-enabled business: the practitioner also gets the full scope list
          if (business.workforce_assessment_enabled) {
            const scopes = [
              'appointments:read',
              'appointments:write',
              'practitioner_assessments:write',
              'practitioner_assessments:read',
              'eip:request_extension',
              'preventive_health:request_extension'
            ];
            return {
              scope_type: 'practitioner' as const,
              practitioner_uuid: practitionerUuid,
              scope: scopes.join(' ')
            };
          }
          // No assessments: no scope sent, so the backend grants its default practitioner scope
          return { scope_type: 'practitioner' as const, practitioner_uuid: practitionerUuid };
        })
      );
    }

    return throwError(() => new Error('Workforce token: user is not enabled for workforce'));
  }

  /**
   * The selected business out of all the user's businesses in `detail.businesses`.
   *
   *  - A business uuid is selected (the one sent in the request headers): return that business, or
   *    null when it isn't in the list, so workforce stays disabled rather than using another business's flags.
   *  - No business uuid selected yet: fall back to the business matching `active_slug`, then the first one.
   */
  private resolveBusiness(detail: DashboardWorkforceDetail | null): DashboardBusiness | null {
    const businesses = detail?.businesses ?? [];
    const current = this.authService.getBusinessUUID();
    if (current) {
      return businesses.find((b) => b.uuid === current) ?? null;
    }
    const active = businesses.find((b) => b.slug && b.slug === detail?.active_slug);
    return active ?? businesses[0] ?? null;
  }

  /**
   * The exchange matches the caller to the practitioner on email, so pick the practitioner
   * record that belongs to the logged-in user. A wrong pick is refused with 403, not mis-scoped.
   * Falls back to the first active practitioner when no email matches; cached after the first lookup.
   */
  private resolvePractitionerUuid(businessUuid: string): Observable<string> {
    if (this.practitionerUuid) {
      return of(this.practitionerUuid);
    }

    const params = { pageCount: 10, currentPage: 1, status: 'Active' };
    return this.businessMainService.getAllBusinessPractitioners(businessUuid, params).pipe(
      switchMap((response: any) => {
        const list: any[] = response?.data ?? [];
        const email = this.authService.getloggedUser()?.email?.toLowerCase();
        const own = list.find((p) => email && p?.email?.toLowerCase() === email) ?? list[0];
        if (!own?.uuid) {
          return throwError(() => new Error('Workforce token: practitioner record not found'));
        }
        this.practitionerUuid = own.uuid;
        return of(own.uuid as string);
      })
    );
  }

  /**
   * Exchanges the request for a workforce token and stores it with its expiry.
   * The cache key `scope_type|business_uuid|practitioner_uuid|scope` lets getToken tell whether the
   * stored token still matches the selected business.
   */
  private mint(request: PlatformExchangeRequest): Observable<string> {
    const cacheKey = `${request.scope_type}|${request.business_uuid ?? this.authService.getBusinessUUID()}|${
      request.practitioner_uuid ?? ''
    }|${request.scope ?? ''}`;

    return this.httpClient
      .post<PlatformExchangeResponse>(`${Constants.BASE_URL}/token/platform-exchange/`, request)
      .pipe(
        map((response) => {
          // Fall back to the documented 5-minute lifetime if the backend omits expires_in
          this.authService.setWorkforceToken(response.access_token, {
            expiresAt: Date.now() + (response.expires_in ?? 300) * 1000,
            cacheKey
          });
          return response.access_token;
        })
      );
  }
}
